import { put, call, takeLatest, takeEvery, select, cancelled } from 'redux-saga/effects';
import {
  CASE_GENERATE_ANSWERS_BEGIN,
  CASE_GENERATE_ANSWERS_SUCCESS,
  CASE_GENERATE_ANSWERS_FAILURE,
  CASE_GENERATE_ANSWERS_DISMISS_FEEDBACK,
  CASE_GENERATE_ANSWERS_CLEAR,
  CASE_CANCEL_GENERATE_ANSWERS,
  CASE_FETCH_SIGNALR_ANSWER_SUCCESS,
  CASE_SET_TYPED_AI_RESPONSE_COUNT,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { selectUserSettings, withCurrentCaseId } from 'common/selectors';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';

export const generateAnswers = createPromiseAction(CASE_GENERATE_ANSWERS_BEGIN);

export const cancelGenerateAnswers = () => ({
  type: CASE_CANCEL_GENERATE_ANSWERS,
});

// Store for tracking cancellation state and AbortController
let isCancellationRequested = false;
let currentAbortController = null;

export function dismissGenerateAnswersFeedback() {
  return {
    type: CASE_GENERATE_ANSWERS_DISMISS_FEEDBACK,
  };
}

export function clearAnswers() {
  return {
    type: CASE_GENERATE_ANSWERS_CLEAR,
  };
}

// worker Saga: will be fired on CASE_GENERATE_ANSWERS_BEGIN actions
export function* doGenerateAnswers(action) {
  const {
    payload: {
      caseId,
      summary = [],
      searchQuery = '',
      answerType = 'generate',
      outputType = 'list',
      verbose = true,
      searchHistoryId,
      source,
      promptCategory,
      promptName,
    },
  } = action;

  if (!searchQuery) {
    return yield put({
      type: CASE_GENERATE_ANSWERS_CLEAR,
    });
  }

  // Cancel any existing request
  if (currentAbortController) {
    currentAbortController.abort();
  }

  // Reset cancellation flag and create new AbortController
  isCancellationRequested = false;
  currentAbortController = new AbortController();

  try {
    const userSettings = yield select(selectUserSettings);
    const res = yield call(
      api.post,
      `/cases/${caseId}/answers`,
      {
        ...(searchHistoryId && { searchHistoryId }),
        searchQuery,
        summary,
        answerType,
        outputType,
        verbose,
        ...(source && { source }),
        ...(promptCategory && { promptCategory }),
        ...(promptName && { promptName }),
        wrapMatchesWithContext:
          (userSettings.aiSettings && userSettings.aiSettings.wrapMatchesWithContext) || false,
        useDefaultPrompt:
          userSettings.aiSettings &&
          userSettings.aiSettings.useDefaultPrompt &&
          typeof userSettings.aiSettings.useDefaultPrompt === 'boolean'
            ? userSettings.aiSettings.useDefaultPrompt
            : true,
        ...(userSettings.aiSettings &&
          !userSettings.aiSettings.useDefaultPrompt && {
            systemPrompt: (userSettings.aiSettings && userSettings.aiSettings.systemPrompt) || '',
            sourcePrompt: (userSettings.aiSettings && userSettings.aiSettings.sourcePrompt) || '',
          }),
      },
      { signal: currentAbortController.signal },
    );

    // Check if cancellation was requested while the call was in progress
    if (isCancellationRequested) {
      // Request was cancelled after it completed - resolve as cancelled
      currentAbortController = null;
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Clear the controller on successful completion
    currentAbortController = null;

    if (res && res.error) {
      yield put({
        type: CASE_GENERATE_ANSWERS_FAILURE,
        feedback: {
          message: 'feedback.generateAnswersFailure',
          error: res.error,
        },
      });
      return yield call(rejectPromiseAction, action, res.error);
    }

    yield put({
      type: CASE_GENERATE_ANSWERS_SUCCESS,
      data: res,
    });
    return yield call(resolvePromiseAction, action, res);
  } catch (error) {
    // Clear the controller on error
    currentAbortController = null;

    // Check if it was an abort error (cancellation)
    if (
      error.name === 'AbortError' ||
      (error.message &&
        (error.message.includes('aborted') || error.message.includes('signal is aborted'))) ||
      isCancellationRequested
    ) {
      // Request was cancelled - resolve as cancelled to prevent promise rejection
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Handle other errors
    yield put({
      type: CASE_GENERATE_ANSWERS_FAILURE,
      feedback: {
        message: 'feedback.generateAnswersFailure',
        error: error.message,
      },
    });
    yield call(rejectPromiseAction, action, error);
  } finally {
    // Handle saga cancellation
    if (yield cancelled()) {
      if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
      }
      isCancellationRequested = true;
    }
  }
}

// worker Saga: will be fired on CASE_CANCEL_GENERATE_ANSWERS actions
// eslint-disable-next-line require-yield
export function* doCancelGenerateAnswers() {
  // Set the cancellation flag
  isCancellationRequested = true;

  // Abort the current HTTP request
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

export function* watchGenerateAnswers() {
  yield takeLatest(generateAnswers, withCurrentCaseId(doGenerateAnswers));
}

export function* watchCancelGenerateAnswers() {
  yield takeEvery(CASE_CANCEL_GENERATE_ANSWERS, doCancelGenerateAnswers);
}

export function useGenerateAnswers() {
  const dispatch = useDispatch();

  const {
    searchQuery,
    answers,
    generateAnswersPending,
    generateAnswersFeedback,
    aiToolsResponse,
    typedResponseCount,
  } = useSelector(
    (state) => ({
      searchQuery: state.case.searchQuery,
      answers: state.case.answers,
      aiToolsResponse: state.case.aiToolsResponse,
      typedResponseCount: state.case.typedResponseCount,
      generateAnswersPending: state.case.generateAnswersPending,
      generateAnswersFeedback: state.case.generateAnswersFeedback,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(generateAnswers(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissGenerateAnswersFeedback());
  }, [dispatch]);

  const boundClearAnswers = useCallback(() => {
    return dispatch(clearAnswers());
  }, [dispatch]);

  const boundCancelAction = useCallback(() => {
    return dispatch(cancelGenerateAnswers());
  }, [dispatch]);

  return {
    searchQuery,
    answers,
    aiToolsResponse,
    typedResponseCount,
    generateAnswers: boundAction,
    generateAnswersPending,
    generateAnswersFeedback,
    dismissGenerateAnswersFeedback: boundDismissFeedback,
    clearAnswers: boundClearAnswers,
    cancelGenerateAnswers: boundCancelAction,
  };
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case CASE_GENERATE_ANSWERS_BEGIN + '.TRIGGER':
      return {
        ...state,
        generateAnswersPending: true,
        generateAnswersFeedback: action.feedback,
        answers: null,
        searchQuery: action.payload.searchQuery,
      };

    case CASE_GENERATE_ANSWERS_SUCCESS:
      return {
        ...state,
        generateAnswersPending: false,
        generateAnswersFeedback: action.feedback,
        answers: action.data.answer,
      };

    case CASE_FETCH_SIGNALR_ANSWER_SUCCESS:
      return {
        ...state,
        generateAnswersPending: false,
        generateAnswersFeedback: action.feedback,
        aiToolsResponse: [...state.aiToolsResponse, action.data],
      };

    case CASE_SET_TYPED_AI_RESPONSE_COUNT:
      return {
        ...state,
        typedResponseCount: action.data,
      };

    case CASE_GENERATE_ANSWERS_FAILURE:
      return {
        ...state,
        generateAnswersPending: false,
        generateAnswersFeedback: action.feedback,
      };

    case CASE_GENERATE_ANSWERS_DISMISS_FEEDBACK:
      return {
        ...state,
        generateAnswersFeedback: null,
      };

    case CASE_GENERATE_ANSWERS_CLEAR:
      return {
        ...state,
        generateAnswersPending: false,
        searchQuery: '',
        answers: null,
        generateAnswersFeedback: action.feedback,
      };

    case CASE_CANCEL_GENERATE_ANSWERS:
      return {
        ...state,
        generateAnswersPending: false,
        generateAnswersFeedback: null,
      };

    default:
      return state;
  }
}
